import React, { useState, useEffect } from 'react';
import {
    Grid, Typography, TextField, useMediaQuery,
    ToggleButton, ToggleButtonGroup,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Button as MuiButton,
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import { makeStyles } from '@mui/styles';
import UsersCombo from '../components/UsersCombo';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import Button from "components/CustomButtons/Button.js";
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

const MAX_DEBIT = 50000;

const useStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    '@global': {
        body: {
            backgroundColor: theme.palette.common.white,
        },
    },
    container: {
        maxWidth: "50vw",
        marginTop: theme.spacing(1),
        backgroundColor: MAIN_COLOR,
        alignContent: 'center',
        borderRadius: "8px",
    },
    container1: {
        backgroundColor: colors.Header_Text,
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        padding: '30px',
        width: '100%',
        top: "19px",
        boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
    },
    title: {
        color: colors.WHITE,
        padding: '10px',
        fontSize: 18,
        fontFamily: FONT_FAMILY
    },
    items: {
        margin: 0,
        width: '100%'
    },
    input: {
        fontSize: 18,
        color: colors.BLACK,
        fontFamily: FONT_FAMILY
    },
    buttonStyle: {
        margin: 0,
        width: '100%',
        height: 40,
        borderRadius: "30px",
        backgroundColor: MAIN_COLOR,
        marginTop: '15px',
        color: colors.WHITE,
        fontWeight: "bold",
        fontFamily: FONT_FAMILY
    },
    label: {
        transformOrigin: "top right",
        right: 0,
        left: "auto"
    },
    inputRtl: {
        "& label": {
            right: 20,
            left: "auto",
            fontFamily: FONT_FAMILY
        },
        "& legend": {
            textAlign: "right",
        },
        "& label.Mui-focused": {
            color: MAIN_COLOR,
            fontFamily: FONT_FAMILY
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: MAIN_COLOR,
        },
        "& .MuiFilledInput-underline:after": {
            borderBottomColor: MAIN_COLOR,
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: MAIN_COLOR,
            },
        },
        fontFamily: FONT_FAMILY
    },
    textField: {
        "& label": {
            fontFamily: FONT_FAMILY
        },
        "& label.Mui-focused": {
            color: MAIN_COLOR,
            fontFamily: FONT_FAMILY
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: MAIN_COLOR,
        },
        "& .MuiFilledInput-underline:after": {
            borderBottomColor: MAIN_COLOR,
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: MAIN_COLOR,
            },
        },
        fontFamily: FONT_FAMILY
    },
    selectField: {
        color: colors.BLACK,
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: MAIN_COLOR,
        },
    },
}));

export default function AddMoney(props) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { addToWallet, debitWallet, RequestPushMsg } = api;
    const settings = useSelector(state => state.settingsdata.settings);
    const dispatch = useDispatch();
    const classes = useStyles();
    const userdata = useSelector(state => state.usersdata);
    const [users, setUsers] = useState(null);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
    const [userCombo, setUserCombo] = useState(null);
    const [amount, setAmount] = useState(0);
    const [opType, setOpType] = useState('credit');
    const [reason, setReason] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const isMidScreen = useMediaQuery('(max-width:800px)');

    const sym = settings?.symbol || '';
    const dec = settings?.decimal ?? 2;

    const selectedUserBalance = React.useMemo(() => {
        if (!userCombo || !userdata.users) return null;
        const u = userdata.users.find(u => u.id === userCombo.uid);
        return u ? parseFloat(u.walletBalance || 0) : null;
    }, [userCombo, userdata.users]);

    useEffect(() => {
        if (userdata.users) {
            const arr = userdata.users.map(user => ({
                firstName: user.firstName,
                lastName: user.lastName,
                mobile: user.mobile,
                email: user.email,
                uid: user.id,
                desc: user.firstName + ' ' + user.lastName + ' (' + (settings.AllowCriticalEditsAdmin ? user.mobile : t("hidden_demo")) + ') ' + (settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo")),
                pushToken: user.pushToken || '',
                langLocale: user.lang?.langLocale || ''
            }));
            setUsers(arr);
        }
    }, [userdata.users, settings.AllowCriticalEditsAdmin, t]);

    const handleSubmit = () => {
        if (!userCombo?.uid) {
            setCommonAlert({ open: true, msg: t('no_details_error') });
            return;
        }
        if (!amount || amount <= 0) {
            setCommonAlert({ open: true, msg: t('valid_amount') });
            return;
        }
        if (opType === 'debit') {
            if (!reason || reason.trim().length < 5) {
                setCommonAlert({ open: true, msg: t('debit_reason_required') });
                return;
            }
            if (amount > MAX_DEBIT) {
                setCommonAlert({ open: true, msg: t('debit_max_exceeded') });
                return;
            }
            if (selectedUserBalance !== null && amount > selectedUserBalance) {
                setCommonAlert({ open: true, msg: t('debit_exceeds_balance') });
                return;
            }
            setConfirmOpen(true);
        } else {
            dispatch(addToWallet(userCombo.uid, amount));
            setCommonAlert({ open: true, msg: t('success') });
            setAmount(0);
            setUserCombo(null);
        }
    };

    const getDebitMsg = (langLocale) => {
        if (langLocale && langLocale.startsWith('ar')) return 'تم خصم مبلغ من محفظتك';
        if (langLocale && langLocale.startsWith('fr')) return 'Un montant a été débité de votre portefeuille';
        return 'An amount has been debited from your wallet';
    };

    const handleConfirmDebit = () => {
        setConfirmOpen(false);
        dispatch(debitWallet(userCombo.uid, amount, reason.trim()));
        if (userCombo.pushToken) {
            RequestPushMsg(userCombo.pushToken, {
                title: settings?.notification_title || 'Sawi',
                msg: getDebitMsg(userCombo.langLocale),
                screen: 'Wallet'
            });
        }
        setCommonAlert({ open: true, msg: t('success') });
        setAmount(0);
        setReason('');
        setUserCombo(null);
    };

    const handleCommonAlertClose = (e) => {
        e.preventDefault();
        setCommonAlert({ open: false, msg: '' });
    };

    const titleText = opType === 'debit' ? t('debit_wallet_title') : t('add_to_wallet_title');
    const btnText = opType === 'debit' ? t('debit_from_wallet') : t('add_to_wallet');
    const btnColor = opType === 'debit' ? colors.RED : MAIN_COLOR;

    return (
        <div className={classes.container} style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr', maxWidth: isMidScreen && "100%" }}>
            <Grid item xs={12} sm={12} md={8} lg={8}>
                <Grid item>
                    <Grid item xs={12}>
                        <Typography className={classes.title} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                            {titleText}
                        </Typography>
                    </Grid>
                    <div className={classes.container1}>
                        <Grid container spacing={2}>
                            {/* Operation type toggle */}
                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                                <ToggleButtonGroup
                                    value={opType}
                                    exclusive
                                    onChange={(_, v) => { if (v) { setOpType(v); setReason(''); } }}
                                    size="small"
                                >
                                    <ToggleButton value="credit" style={{ fontFamily: FONT_FAMILY, minWidth: 120, fontWeight: 'bold', backgroundColor: opType === 'credit' ? colors.GREEN : undefined, color: opType === 'credit' ? colors.WHITE : undefined }}>
                                        {t('credit_operation')}
                                    </ToggleButton>
                                    <ToggleButton value="debit" style={{ fontFamily: FONT_FAMILY, minWidth: 120, fontWeight: 'bold', backgroundColor: opType === 'debit' ? colors.RED : undefined, color: opType === 'debit' ? colors.WHITE : undefined }}>
                                        {t('debit_operation')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            {/* User selector */}
                            <Grid item xs={12}>
                                {users ?
                                    <UsersCombo
                                        className={classes.items}
                                        placeholder={t('select_user')}
                                        users={users}
                                        value={userCombo}
                                        onChange={(event, newValue) => {
                                            setUserCombo(newValue);
                                        }}
                                    />
                                    : null}
                                {userCombo && selectedUserBalance !== null && (
                                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 13, color: colors.BLACK, marginTop: 6 }}>
                                        {t('current_balance')}: <strong>{settings.swipe_symbol ? `${selectedUserBalance.toFixed(dec)} ${sym}` : `${sym} ${selectedUserBalance.toFixed(dec)}`}</strong>
                                    </Typography>
                                )}
                            </Grid>

                            {/* Amount */}
                            <Grid item xs={12} sm={12} md={12} lg={opType === 'debit' ? 12 : 6} xl={opType === 'debit' ? 12 : 6}>
                                <TextField
                                    label={t('amount')}
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
                                    InputProps={{ className: classes.input }}
                                    value={amount}
                                    onChange={event => {
                                        const v = event.target.value;
                                        setAmount(v === '' || v === null ? 0 : parseFloat(v));
                                    }}
                                />
                            </Grid>

                            {/* Reason (debit only) */}
                            {opType === 'debit' && (
                                <Grid item xs={12}>
                                    <TextField
                                        label={t('debit_reason')}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        className={isRTL === 'rtl' ? classes.inputRtl : classes.textField}
                                        InputProps={{ className: classes.input }}
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        placeholder={t('debit_reason_required')}
                                    />
                                </Grid>
                            )}

                            {/* Submit button */}
                            <Grid item xs={12} sm={12} md={12} lg={opType === 'debit' ? 12 : 6} xl={opType === 'debit' ? 12 : 6}>
                                <Button
                                    size="lg"
                                    onClick={handleSubmit}
                                    variant="contained"
                                    color="secondaryButton"
                                    className={classes.buttonStyle}
                                    style={{ backgroundColor: btnColor }}
                                >
                                    {btnText}
                                </Button>
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
            </Grid>

            {/* Debit confirmation dialog */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle style={{ fontFamily: FONT_FAMILY }}>{t('debit_confirm_title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ fontFamily: FONT_FAMILY }}>
                        {t('debit_confirm_msg')
                            .replace('{name}', userCombo ? `${userCombo.firstName} ${userCombo.lastName}` : '')
                            .replace('{amount}', settings.swipe_symbol ? `${parseFloat(amount).toFixed(dec)} ${sym}` : `${sym} ${parseFloat(amount).toFixed(dec)}`)
                            .replace('{reason}', reason)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={() => setConfirmOpen(false)} style={{ fontFamily: FONT_FAMILY, color: colors.BLACK }}>
                        {t('cancel')}
                    </MuiButton>
                    <MuiButton onClick={handleConfirmDebit} style={{ fontFamily: FONT_FAMILY, backgroundColor: colors.RED, color: colors.WHITE, padding: '6px 16px' }}>
                        {t('confirm')}
                    </MuiButton>
                </DialogActions>
            </Dialog>

            <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
        </div>
    );
}
